import * as React from 'react'

import { useMutation } from '@apollo/client'
import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import {
  ChangeAssetOwnershipDocument,
  ItAssetDetailsQuery,
  ItAssetsTableQuery,
} from 'generated/resources/graphql'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  AssignmentForm,
  type AssignmentFormData,
  type AssignmentType,
  type DeviceStatus,
} from 'modules/it_management/components/AssignmentForm'
import { useAssignmentFormSaveDisabled } from 'modules/it_management/hooks/useAssignmentFormSaveDisabled'

type Asset =
  | ItAssetsTableQuery['itManagement']['itAssetsConnection']['nodes'][number]
  | ItAssetDetailsQuery['itManagement']['itAsset']

interface Props {
  asset: Asset
  onClose: () => void
}

const isValidDeviceStatus = (status: string | null | undefined): status is DeviceStatus => {
  return status === 'in_stock' || status === 'maintenance' || status === 'retired'
}

export const ChangeAssetOwnershipModal = ({ asset, onClose }: Props) => {
  const [changeAssetOwnership, { loading: assigning }] = useMutation(ChangeAssetOwnershipDocument)

  const getInitialAssignmentType = (): AssignmentType => {
    if (asset?.owner) return 'person'
    if (asset?.workplace) return 'workplace'
    if (asset?.team) return 'team'
    if (asset?.location) return 'space'
    return 'none'
  }

  const getInitialDeviceStatus = (): DeviceStatus => {
    const assignmentType = getInitialAssignmentType()
    if (assignmentType === 'none' && isValidDeviceStatus(asset?.status)) {
      return asset.status
    }
    return 'in_stock'
  }

  const [formData, setFormData] = React.useState<AssignmentFormData>({
    assignmentType: getInitialAssignmentType(),
    selectedEmployeeId: asset?.owner?.id,
    selectedWorkplace: asset?.workplace
      ? {
          id: String(asset.workplace.id),
          name: asset.workplace.name || '',
        }
      : null,
    selectedTeam: asset?.team
      ? {
          id: String(asset.team.id),
          name: asset.team.name || '',
        }
      : null,
    selectedLocation: asset?.location
      ? {
          id: String(asset.location.id),
          name: asset.location.name || '',
        }
      : null,
    deviceStatus: getInitialDeviceStatus(),
  })

  const isSaveDisabled = useAssignmentFormSaveDisabled(formData.assignmentType)

  const handleAssignOwnerAsset = async () => {
    if (!asset) return

    try {
      let ownerId: number | null = null
      let locationId: number | null = null
      let teamId: number | null = null
      let workplaceId: number | null = null

      switch (formData.assignmentType) {
        case 'person':
          ownerId = formData.selectedEmployeeId ?? null
          break
        case 'workplace':
          workplaceId = formData.selectedWorkplace ? Number(formData.selectedWorkplace.id) : null
          break
        case 'team':
          teamId = formData.selectedTeam ? Number(formData.selectedTeam.id) : null
          break
        case 'space':
          locationId = formData.selectedLocation ? Number(formData.selectedLocation.id) : null
          break
        case 'none':
          ownerId = null
          locationId = null
          teamId = null
          workplaceId = null
          break
      }

      await changeAssetOwnership({
        variables: {
          id: asset.id,
          ownerId,
          locationId,
          teamId,
          workplaceId,
          status: formData.assignmentType === 'none' ? formData.deviceStatus : undefined,
        },
        refetchQueries: ['ItAssetsTable', 'ItAssetDetails', 'ItAssetsStatusCounts'],
        awaitRefetchQueries: true,
      })

      track(trackingEvents.itManagement.assigmentEquipment.submit, {
        origin: 'device_details',
        assign_to: formData.assignmentType,
      })

      app.setFlash({
        type: 'correct',
        message:
          formData.assignmentType !== 'none'
            ? i18n.t('it_management.assets.assign_owner_modal.success_message')
            : i18n.t('it_management.assets.assign_owner_modal.unassigned_message'),
      })
      onClose()
    } catch (error) {
      track(trackingEvents.itManagement.assigmentEquipment.error)
      app.setFlash({
        type: 'wrong',
        message: i18n.t('it_management.assets.assign_owner_modal.error_message'),
      })
    }
  }

  const assetName = asset
    ? asset.itAssetModel?.brand && asset.itAssetModel?.name
      ? `${asset.itAssetModel.brand} ${asset.itAssetModel.name}`
      : asset.serialNumber || i18n.t('it_management.assets.assign_owner_modal.unknown_asset')
    : i18n.t('it_management.assets.assign_owner_modal.unknown_asset')

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t('it_management.assets.assign_owner_modal.title')}
      primaryAction={{
        label: i18n.t('it_management.assets.assign_owner_modal.assign'),
        onClick: handleAssignOwnerAsset,
        loading: assigning,
        disabled: assigning || isSaveDisabled,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
        disabled: assigning,
      }}
    >
      <Box gap="s16">
        <Box background="grey100" padding="s16" borderRadius={{ all: 'abs004' }}>
          <Text size="200" color="grey800" weight="semibold">
            {i18n.t('it_management.assets.presets.asset')}: {assetName}
          </Text>
        </Box>
        <AssignmentForm value={formData} onChange={setFormData} />
      </Box>
    </F0Dialog>
  )
}
